<template>
  <div>
    <h1 class="title-page">
      <v-icon class="material-icons-outlined">import_contacts</v-icon>
      <br />表紙の名前を入力
    </h1>

    <v-card class="card">
      <h2 class="title-bar">園・施設名と名前を入力</h2>
      <section>
        <div ref="previewElement" class="preview">
          <div
            :class="[
              'cover-preview-wrapper',
              { 'cover-preview-pastel': selectedDesignType === 'pastel' }
            ]"
            :style="{
              width: previewWidth + 'px',
              height: totalCoverHeight + 'px',
              '--scale-factor': scaleToPreview
            }"
          >
            <component
              :is="
                selectedDesignType === 'pastel' ? 'PastelCover' : 'SimpleCover'
              "
              :organization-name="localOrganizationName"
              :kid-name="localKidName"
              :display-year-month="displayYearMonth"
              :start-month="startMonth"
              :end-month="endMonth"
            ></component>
          </div>
        </div>
        <v-text-field
          v-model="localOrganizationName"
          label="園・施設名"
          outline
          :rules="organizationNameRules"
          :error="organizationNameError"
          :error-messages="organizationNameErrorMessages"
          :counter="organizationNameMax"
          :hint="`${organizationNameMax}文字以内で入力してください`"
          persistent-hint
        ></v-text-field>

        <v-text-field
          v-model="localKidName"
          label="名前"
          outline
          :rules="kidNameRules"
          :error="kidNameError"
          :error-messages="kidNameErrorMessages"
          :counter="kidNameMax"
          :hint="`${kidNameMax}文字以内で入力してください`"
          persistent-hint
        ></v-text-field>

        <p class="sub-text">
          【特殊文字の利用について】<br />
          記号や絵文字、環境依存文字は、文字化け・別の文字に置き換わる可能性があります。<br />
          カートに追加する前の「製本の内容確認」画面で、正しく表示されているか必ずご確認ください。
        </p>
      </section>

      <v-layout justify-center column pb-4 align-center>
        <v-btn
          class="v-btn--minwidth"
          color="primary"
          :disabled="!canProceed"
          @click="handleNext"
        >
          次へ進む
        </v-btn>

        <v-btn class="v-btn--minwidth" color="negative" @click="$emit('back')">
          戻る
        </v-btn>
      </v-layout>
    </v-card>
  </div>
</template>

<script>
const ORGANIZATION_NAME_MAX = 19;
const KID_NAME_MAX = 15;

// 共通のバリデーション関数（true か エラーメッセージ文字列を返す）
const validateOrganizationName = value => {
  const length = (value || "").length;
  return (
    (length > 0 && length <= ORGANIZATION_NAME_MAX) ||
    `${ORGANIZATION_NAME_MAX}文字以内で入力してください`
  );
};

const validateKidName = value => {
  const length = (value || "").length;
  return (
    (length > 0 && length <= KID_NAME_MAX) ||
    `${KID_NAME_MAX}文字以内で入力してください`
  );
};

import PastelCover from "../Covers/PastelCover.vue";
import SimpleCover from "../Covers/SimpleCover.vue";
import { COVER_COMMON_SIZES } from "../CoverConstants";

export default {
  name: "EditCoverText",
  components: {
    PastelCover,
    SimpleCover
  },
  props: {
    selectedPeriod: {
      type: Object,
      default: null
    },
    organizationName: {
      type: String,
      default: ""
    },
    kidName: {
      type: String,
      default: ""
    },
    selectedDesignType: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      previewWidth: COVER_COMMON_SIZES.previewWidth,
      // template から使う最大文字数（定数から設定）
      organizationNameMax: ORGANIZATION_NAME_MAX,
      kidNameMax: KID_NAME_MAX,
      // Vuetify の rules 用（バリデーション関数は共通化）
      organizationNameRules: [validateOrganizationName],
      kidNameRules: [validateKidName]
    };
  },
  computed: {
    startMonth() {
      return this.selectedPeriod ? this.selectedPeriod.displayStartMonth : "";
    },
    endMonth() {
      return this.selectedPeriod ? this.selectedPeriod.displayEndMonth : "";
    },
    displayYearMonth() {
      return this.selectedPeriod ? this.selectedPeriod.displayYearMonth : "";
    },
    localOrganizationName: {
      get() {
        return this.organizationName;
      },
      set(value) {
        this.$emit("update:organizationName", value);
      }
    },
    localKidName: {
      get() {
        return this.kidName;
      },
      set(value) {
        this.$emit("update:kidName", value);
      }
    },
    // 以下は初期表示も含めて常に現在値からエラー状態とメッセージを算出
    organizationNameError() {
      return validateOrganizationName(this.localOrganizationName) !== true;
    },
    organizationNameErrorMessages() {
      const result = validateOrganizationName(this.localOrganizationName);
      return result === true ? [] : [result];
    },
    kidNameError() {
      return validateKidName(this.localKidName) !== true;
    },
    kidNameErrorMessages() {
      const result = validateKidName(this.localKidName);
      return result === true ? [] : [result];
    },
    canProceed() {
      const organizationNameLength = this.localOrganizationName.length;
      const kidNameLength = this.localKidName.length;
      return (
        organizationNameLength > 0 &&
        organizationNameLength <= ORGANIZATION_NAME_MAX &&
        kidNameLength > 0 &&
        kidNameLength <= KID_NAME_MAX
      );
    },
    // プレビュー表示用の拡大率（最大0.158）
    scaleToPreview() {
      return this.previewWidth / COVER_COMMON_SIZES.coverWidth;
    },
    totalCoverHeight() {
      return Math.round(COVER_COMMON_SIZES.coverHeight * this.scaleToPreview);
    }
  },
  mounted() {
    // 初期表示時にプレビュー幅を計算(nextTickでDOM確定を待つ)
    this.$nextTick(() => {
      this.updatePreviewWidth();
    });
    // ウィンドウリサイズ時に再計算
    window.addEventListener("resize", this.updatePreviewWidth);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updatePreviewWidth);
  },
  methods: {
    handleNext() {
      if (!this.canProceed) return;
      this.$emit("next");
    },
    // 画面幅に応じてプレビュー幅を計算
    updatePreviewWidth() {
      const previewElement = this.$refs.previewElement;
      if (!previewElement) return;

      // コンテナの幅を取得し、パディング(左右29px*2=58px)を確保
      const containerWidth = previewElement.clientWidth;
      const padding = 58; // プレビューのpadding 29px * 2

      // プレビュー幅を計算(余白を引いて、最大幅を超えないようにする)
      const calculatedWidth = Math.max(0, containerWidth - padding);
      this.previewWidth = Math.min(
        calculatedWidth,
        COVER_COMMON_SIZES.previewWidth
      );
    }
  }
};
</script>

<style scoped>
.card {
  max-width: 443px;
}
.card section {
  padding: 16px;
}
.preview {
  background-color: #f5f5f5;
  width: 100%;
  max-width: 443px;
  display: flex;
  justify-content: center;
  height: 160px;
  overflow: hidden;
  padding: 29px;
  margin-bottom: 16px;
}
/* 表紙プレビューの共通スタイル */
.cover-preview-wrapper {
  position: relative;
  box-shadow: 6.39px 12.77px 12.77px 0px #00000014;
  margin-bottom: 20px;
  border: 1px solid #eaeae8;
  font-weight: 600;
  color: #47372c;
  background-color: white;
}

.cover-preview-pastel {
  background-image: url("../../../../assets/images/combook_binding/cover_front_bg_pastel.png");
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}
</style>
